import {useState, type FormEvent} from 'react';
import {AwButton, AwField, AwSelect} from '@ascension-tracker/ascension-ui';
import {useCreateCustomField, useDeleteCustomField, useProjectCustomFields, type CustomFieldType} from '../hooks/useTasks.js';

const fieldTypeLabels: Record<CustomFieldType, string> = {
  text: 'Text',
  number: 'Number',
  single_select: 'Single select',
  date: 'Date',
  people: 'Person'
};

export function CustomFieldManager({projectId}: {projectId: string}) {
  const fields = useProjectCustomFields(projectId);
  const createField = useCreateCustomField(projectId);
  const deleteField = useDeleteCustomField(projectId);
  const [name, setName] = useState('');
  const [fieldType, setFieldType] = useState<CustomFieldType>('text');
  const [optionsText, setOptionsText] = useState('');

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim()) return;
    const options = fieldType === 'single_select' ? optionsText.split(',').map(value => value.trim()).filter(Boolean) : undefined;
    if (fieldType === 'single_select' && !options?.length) return;
    await createField.mutateAsync({name: name.trim(), fieldType, options});
    setName('');
    setOptionsText('');
  }

  return (
    <div>
      {(fields.data ?? []).map(field => (
        <div key={field.id} className="custom-field-row">
          <strong>{field.name}</strong>
          <small>{fieldTypeLabels[field.fieldType]}{field.options.length > 0 ? ` (${field.options.map(option => option.label).join(', ')})` : ''}</small>
          <button type="button" aria-label={`Delete field ${field.name}`} onClick={() => deleteField.mutate(field.id)}>×</button>
        </div>
      ))}
      <form className="project-form" onSubmit={create}>
        <div className="project-form__row">
          <AwField id="settings-new-field-name" label="Field name" value={name} onChange={event => setName(event.target.value)}/>
          <AwSelect id="settings-new-field-type" label="Type" value={fieldType} onChange={event => setFieldType(event.target.value as CustomFieldType)}>
            {Object.entries(fieldTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </AwSelect>
        </div>
        {fieldType === 'single_select' && (
          <AwField id="settings-new-field-options" label="Options (comma separated)" value={optionsText} onChange={event => setOptionsText(event.target.value)}/>
        )}
        <div className="project-form__actions">
          <AwButton type="submit" variant="secondary" disabled={!name.trim() || (fieldType === 'single_select' && !optionsText.trim())}>Add field</AwButton>
        </div>
      </form>
    </div>
  );
}
